import React, { useCallback, useState } from 'react';

import { HurdleDiscountParams } from 'chaika-frontend-types/frontend-cms';
import { DiscountType } from 'chaika-enums';

import { getDiscountTypeTranslation } from '@utils/enumTranslation';
import { Discount } from '@common/CreationMenuFields/DiscountEditor/DiscountEditor';
import { OptionType } from '@components/Select';

interface Props {
    value: Discount;
    onChange(params: any): void;
}

export function useDiscountEditor({ value, onChange }: Props) {
    const discountTypeOptions = Object.values(DiscountType).map(value => ({
        value,
        label: getDiscountTypeTranslation(value),
    }));

    const [discountType, setDiscountType] = useState(value?.discountType);

    const [discounts, setDiscounts] = useState<Omit<Discount, 'discountType'>>({
        flatDiscount: value?.flatDiscount ?? undefined,
        averageDiscount: value?.averageDiscount ?? undefined,
        hurdleDiscount: value?.hurdleDiscount ?? undefined,
    });

    const onChangeDiscountTypeSwitcher = useCallback(
        ({ value }: OptionType<string>) => {
            const newDiscountType = value as DiscountType;

            const newState: Omit<Discount, 'discountType'> = {
                flatDiscount: undefined,
                averageDiscount: undefined,
                hurdleDiscount: undefined,
            };

            switch (newDiscountType) {
                case DiscountType.Flat:
                    newState.flatDiscount = discounts.flatDiscount;
                    break;
                case DiscountType.Average:
                    newState.averageDiscount = discounts.averageDiscount;
                    break;
                case DiscountType.Hurdle:
                    newState.hurdleDiscount = discounts.hurdleDiscount;
                    break;
            }

            setDiscountType(newDiscountType);
            setDiscounts(newState);

            onChange({ discountType: newDiscountType, ...newState });
        },
        [discounts.averageDiscount, discounts.flatDiscount, discounts.hurdleDiscount, onChange],
    );

    const onChangeNumberField = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
            const value = event.target.value;

            const newState = { discountType, ...discounts, [name]: value };

            setDiscounts(newState);
            onChange(newState);
        },
        [discountType, discounts, onChange],
    );

    const onChangeHurdleField = useCallback(
        (params: HurdleDiscountParams[]) => {
            const newState = { discountType, ...discounts, hurdleDiscount: params };

            setDiscounts(newState);
            onChange(newState);
        },
        [discountType, discounts, onChange],
    );

    return {
        discountType,
        discounts,
        discountTypeOptions,
        onChangeNumberField,
        onChangeHurdleField,
        onChangeDiscountTypeSwitcher,
    };
}
